import { BaseGeometryEditor, LayerProvider, useGeometryEditorStateContext } from '@cibo/mapgl'
import { Box, Grid, Stack } from '@mui/material'
import { FeatureCollection } from '@turf/turf'
import { Feature, Geometry } from 'geojson'
import { flatten } from 'ramda'
import { useCallback, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useInventory } from '../../../../../features/Inventories/queries/useInventory'
import { InventoryMapLayers } from './InventoryMapLayers'
import { MapLayerOverlay } from './MapLayerOverlay'
import { SelectMap } from './SelectMap'

export const BoundariesMap = () => {
  const { inventoryId } = useParams()
  const { data: inventory, dataUpdatedAt } = useInventory(inventoryId)
  const [params, setParams] = useSearchParams()
  const isDrawing = params.get('step') === 'drawing'
  const isSelecting = params.get('step') === 'selectArea'
  const isEditing = params.get('step') === 'details'
  const areaId = params.get('id')
  const [selectedFeatures, setSelectedFeatures] = useState<
    Feature<Geometry, { resourceId: string }>[]
  >()
  const [inventoryFeatures, setInventoryFeatures] = useState<
    Feature<Geometry, { resourceId: string }>[] | undefined
  >()
  const [selectedFips, setSelectedFips] = useState<string[]>([])
  const [inventoryFips, setInventoryFips] = useState<string[]>([])
  const [boundaryTouched, setBoundaryTouched] = useState<boolean>(false)
  const { resetGeometryEditor } = useGeometryEditorStateContext()

  useEffect(() => {
    if (!inventory) return
    const fips = flatten(
      inventory?.item.boundaries.map(boundary =>
        boundary.areaDef.filter(area => area.type === 'fips')
      )
    )
      // above verifies the type, which is determined by the presence of the fips
      // eslint-disable-next-line
      .map(areaDef => areaDef.fips!)
    const features = flatten(
      inventory?.item.boundaries.map(boundary =>
        boundary.areaDef.filter(area => area.type === 'feature')
      )
      // above verifies the type, which is determined by the presence of the feature
      // eslint-disable-next-line
    ).map(
      ({ feature }, index) =>
        ({
          type: 'Feature',
          properties: { resourceId: `${areaId}-${index}` },
          // above verifies the type, which is determined by the presence of the feature
          // eslint-disable-next-line
          geometry: feature!.geometry,
        } as Feature<Geometry, { resourceId: string }>)
    )
    setInventoryFips(fips)
    !!features && setInventoryFeatures(features)
  }, [dataUpdatedAt])

  useEffect(() => {
    const boundary = inventory?.item.boundaries.find(area => area.boundaryId === areaId)
    if (boundary) {
      const features: Feature<Geometry, { resourceId: string }>[] =
        boundary.areaDef
          .filter(area => area.type === 'feature')
          .map(({ feature }, index) => ({
            type: 'Feature',
            properties: { resourceId: `${areaId}-${index}` },
            // above verifies the type, which is determined by the presence of the feature
            // eslint-disable-next-line
            geometry: feature!.geometry,
          })) || []
      const fips =
        boundary.areaDef
          .filter(area => area.type === 'fips')
          // above verifies the type, which is determined by the presence of the fips
          // eslint-disable-next-line
          .map(areaDef => areaDef.fips!) || []
      setSelectedFeatures(features)
      setSelectedFips(fips)
    }
  }, [areaId, dataUpdatedAt])

  const onChangeDrawnBoundary = useCallback(
    (props: { features: FeatureCollection; touched: boolean }) => {
      const featuresWithResourceId = props.features.features.map(
        (feature, index) =>
          ({
            type: 'Feature',
            properties: { resourceId: `currentlyDrawing-${index}` },
            // above verifies the type, which is determined by the presence of the feature
            // eslint-disable-next-line
            geometry: feature!.geometry,
          } as Feature<Geometry, { resourceId: string }>)
      )
      setSelectedFeatures(featuresWithResourceId)
      setBoundaryTouched(props.touched)
    },
    []
  )

  return (
    <Stack flex={1}>
      <Grid sm={12} md={8} lg={10} sx={{ height: '100%', display: 'flex' }} item>
        <Box sx={{ flex: 1, display: 'grid' }}>
          <LayerProvider>
            <div
              style={{
                background: 'transparent',
                gridRow: 1,
                gridColumn: 1,
                zIndex: 103,
                pointerEvents: 'none',
                padding: '16px',
              }}
            >
              <MapLayerOverlay
                clearSelectedFeatures={() => {
                  resetGeometryEditor()
                  setSelectedFeatures([])
                }}
                clearSelectedFips={() => setSelectedFips([])}
                features={selectedFeatures}
                boundaryTouched={boundaryTouched}
                selectedFips={selectedFips}
              />
            </div>
            <BaseGeometryEditor
              displayTools={isDrawing}
              disabled={!isDrawing}
              onChange={onChangeDrawnBoundary}
              onBack={() => {
                resetGeometryEditor()
                setParams({ step: 'creating' })
              }}
              onNext={() => {
                setParams({ step: 'details', type: 'draw' })
              }}
              style={{ gridRow: 1, gridColumn: 1, zIndex: isDrawing ? 102 : 100 }}
              Layers={<InventoryMapLayers />}
            />
            {/* Mysteriously the below div is necessary because the maptiles on GeometryEditor 
                  in satellite appear to be about 95% opacity above a certain zoom level */}
            <div
              style={{
                background: '#fff',
                gridRow: 1,
                gridColumn: 1,
                zIndex: 101,
              }}
            />
            <SelectMap
              isClickable={isSelecting}
              displayOverlay={!isDrawing}
              currentFips={isSelecting || isEditing ? selectedFips : inventoryFips}
              currentFeatures={isEditing ? selectedFeatures : isSelecting ? [] : inventoryFeatures}
              onSelectionChanged={fips => {
                setBoundaryTouched(true)
                setSelectedFips(fips)
              }}
              sx={{ gridRow: 1, gridColumn: 1, zIndex: isDrawing ? 100 : 102 }}
            >
              <InventoryMapLayers />
            </SelectMap>
          </LayerProvider>
        </Box>
      </Grid>
    </Stack>
  )
}
